// Master-Detail layout for the /categories admin page.
//
// Desktop (>=768px): tree on the left, detail panel on the right.
// Mobile (<768px): tree fills the screen; tapping a row swaps in the detail
// panel (same component, just a different responsive class).

import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/lib/auth/store";
import { translateApiError } from "@/lib/errors/error-map";
import {
  countCategories,
  findCategory,
  sumProductCount,
  type Category,
} from "./types";
import { useCategoriesQuery, useDeleteCategory } from "./use-categories";
import { CategoryTree } from "./category-tree";
import { CategoryDetailPanel } from "./category-detail-panel";
import { CategoryFormDialog } from "./category-form-dialog";

type FormState =
  | { mode: "create"; parentId: string | null }
  | { mode: "edit"; cat: Category }
  | null;

export function CategoriesPage() {
  const user = useAuthStore((s) => s.user);
  const canMutate = user?.role === "admin" || user?.role === "manager" || user?.role === "sales";

  const { data, isLoading } = useCategoriesQuery();
  const cats = data ?? [];
  const deleteMutation = useDeleteCategory();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<FormState>(null);

  // Drop the selection silently if the underlying category disappears
  // (e.g. after a delete or a refetch removes it).
  useEffect(() => {
    if (selectedId && !findCategory(cats, selectedId)) setSelectedId(null);
  }, [cats, selectedId]);

  const totalCats = countCategories(cats);
  const totalProducts = sumProductCount(cats);

  function handleDelete(cat: Category) {
    const msg =
      cat.children.length > 0
        ? `Danh mục "${cat.name}" có ${cat.children.length} danh mục con. Vẫn xoá?`
        : `Xoá danh mục "${cat.name}"?`;
    if (!window.confirm(msg)) return;
    deleteMutation.mutate(cat.id, {
      onSuccess: () => {
        if (selectedId === cat.id) setSelectedId(null);
      },
      onError: (e) => window.alert(translateApiError(e)),
    });
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Danh mục</h1>
          <p className="text-xs text-muted-foreground">
            {totalCats} nhóm · {totalProducts} sản phẩm
          </p>
        </div>
        {canMutate && (
          <Button size="sm" onClick={() => setForm({ mode: "create", parentId: null })}>
            <Plus size={16} />
            <span>Thêm danh mục</span>
          </Button>
        )}
      </header>

      <div className="flex-1 overflow-hidden rounded-lg border bg-card">
        {isLoading ? (
          <TreeSkeleton />
        ) : (
          <div className="grid h-full md:grid-cols-[minmax(260px,360px)_1fr]">
            {/* Tree pane — hidden on mobile when a row is selected */}
            <div
              className={
                "h-full min-h-0 overflow-hidden border-r pt-2 " +
                (selectedId ? "hidden md:block" : "block")
              }
            >
              <CategoryTree
                cats={cats}
                query={query}
                onQueryChange={setQuery}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            </div>

            {/* Detail pane — full-width on mobile when a row is selected */}
            <div
              className={
                "h-full min-h-0 overflow-hidden " +
                (selectedId ? "block" : "hidden md:block")
              }
            >
              <CategoryDetailPanel
                cats={cats}
                selectedId={selectedId}
                canMutate={canMutate}
                onEdit={(cat) => setForm({ mode: "edit", cat })}
                onAddChild={(parentId) => setForm({ mode: "create", parentId })}
                onDelete={handleDelete}
                onBack={selectedId ? () => setSelectedId(null) : undefined}
              />
            </div>
          </div>
        )}
      </div>

      {form && (
        <CategoryFormDialog
          cats={cats}
          editing={form.mode === "edit" ? form.cat : null}
          defaultParentId={form.mode === "create" ? form.parentId : null}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

function TreeSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
